/**
 * Generates a 1200×630 PNG journey card with the globe screenshot
 * as the hero image and route stats overlaid at the bottom.
 *
 * Layout:
 *   - Full-bleed globe screenshot (cover-fit)
 *   - Top-left: "PacketVoyager" brand
 *   - Bottom gradient overlay with route info + stats
 */

import { type Hop } from "@/store/useTraceStore";
import { PHYSICS } from "@/config/constants";

const WIDTH = 1200;
const HEIGHT = 630;

export async function generateJourneyCard(
  screenshotDataUrl: string,
  hops: Hop[],
  targetUrl: string
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext("2d")!;

  // ── 1. Dark background (visible if screenshot doesn't cover fully) ──
  ctx.fillStyle = "#050510";
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // ── 2. Globe screenshot (cover-fit) ──
  const img = await loadImage(screenshotDataUrl);
  drawCover(ctx, img, WIDTH, HEIGHT);

  // ── 3. Bottom gradient overlay ──
  const botGrad = ctx.createLinearGradient(0, HEIGHT * 0.5, 0, HEIGHT);
  botGrad.addColorStop(0, "rgba(5, 5, 16, 0)");
  botGrad.addColorStop(0.35, "rgba(5, 5, 16, 0.65)");
  botGrad.addColorStop(1, "rgba(5, 5, 16, 0.95)");
  ctx.fillStyle = botGrad;
  ctx.fillRect(0, HEIGHT * 0.5, WIDTH, HEIGHT * 0.5);

  // ── 4. Top gradient for brand readability ──
  const topGrad = ctx.createLinearGradient(0, 0, 0, 90);
  topGrad.addColorStop(0, "rgba(5, 5, 16, 0.6)");
  topGrad.addColorStop(1, "rgba(5, 5, 16, 0)");
  ctx.fillStyle = topGrad;
  ctx.fillRect(0, 0, WIDTH, 90);

  // ── 5. Ensure web fonts are available for canvas ──
  await document.fonts.ready;

  // ── 6. Brand: "PacketVoyager" ──
  ctx.font = "800 22px 'Plus Jakarta Sans', system-ui, sans-serif";
  ctx.fillStyle = "#E4E4E7";
  const packetW = ctx.measureText("Packet").width;
  ctx.fillText("Packet", 40, 44);
  ctx.fillStyle = "#06B6D4";
  ctx.fillText("Voyager", 40 + packetW, 44);

  // ── 7. Compute stats ──
  const origin = hops[0];
  const dest = hops[hops.length - 1];
  const totalLatency = dest.cumLatency;
  const totalDistance = hops.reduce((sum, hop) => {
    const maxPlausible = hop.latency * 300;
    return sum + Math.min(hop.distance, maxPlausible);
  }, 0);
  const lightFiberMs = Math.round(
    (totalDistance / PHYSICS.SPEED_OF_LIGHT_FIBER) * 1000
  );
  const lightEfficiency =
    totalLatency > 0 ? Math.round((lightFiberMs / totalLatency) * 100) : 0;
  const cableCrossings = [
    ...new Set(hops.filter((h) => h.cable).map((h) => h.cable!.name)),
  ];

  // ── 8. Thin separator ──
  ctx.strokeStyle = "rgba(6, 182, 212, 0.15)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(40, HEIGHT - 160);
  ctx.lineTo(WIDTH - 40, HEIGHT - 160);
  ctx.stroke();

  // ── 9. Domain (left) + Latency (right) ──
  ctx.font = "700 30px system-ui, -apple-system, sans-serif";
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText(targetUrl, 40, HEIGHT - 120);

  ctx.font =
    "700 30px 'Geist Mono', 'SF Mono', 'Cascadia Code', Consolas, monospace";
  ctx.fillStyle = "#06B6D4";
  const latencyStr = `${totalLatency}ms`;
  const latencyW = ctx.measureText(latencyStr).width;
  ctx.fillText(latencyStr, WIDTH - 40 - latencyW, HEIGHT - 120);

  // ── 10. Route (left) + Distance (right) ──
  ctx.font = "400 18px system-ui, -apple-system, sans-serif";
  ctx.fillStyle = "#A1A1AA";
  ctx.fillText(
    `${origin.city}, ${origin.country}  \u2192  ${dest.city}, ${dest.country}`,
    40,
    HEIGHT - 86
  );

  ctx.font =
    "500 18px 'Geist Mono', 'SF Mono', 'Cascadia Code', Consolas, monospace";
  const distStr = `${totalDistance.toLocaleString()} km`;
  const distW = ctx.measureText(distStr).width;
  ctx.fillText(distStr, WIDTH - 40 - distW, HEIGHT - 86);

  // ── 11. Stats line ──
  ctx.font =
    "500 14px 'Geist Mono', 'SF Mono', 'Cascadia Code', Consolas, monospace";
  ctx.fillStyle = "#71717A";
  ctx.fillText(
    `${hops.length} hops  \u2022  ${lightEfficiency}% speed of light`,
    40,
    HEIGHT - 52
  );

  // ── 12. Cable crossings (if any) ──
  if (cableCrossings.length > 0) {
    ctx.fillStyle = "#0891B2";
    ctx.font =
      "500 14px 'Geist Mono', 'SF Mono', 'Cascadia Code', Consolas, monospace";
    ctx.fillText(`via ${cableCrossings.join(", ")}`, 40, HEIGHT - 28);
  }

  // ── Convert to blob ──
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Canvas toBlob failed")),
      "image/png"
    );
  });
}

/* ─── Helpers ─── */

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Draw image covering the target area (like CSS object-fit: cover) */
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  w: number,
  h: number
) {
  const imgAspect = img.width / img.height;
  const cardAspect = w / h;
  let sx = 0,
    sy = 0,
    sw = img.width,
    sh = img.height;
  if (imgAspect > cardAspect) {
    sw = img.height * cardAspect;
    sx = (img.width - sw) / 2;
  } else {
    sh = img.width / cardAspect;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
}
